import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet, Loader2 } from "lucide-react";

// P46 (2026-10-21): el número de load que lleva tráfico en su archivo TRAFICO (hoja LOGISTICA USA,
// "Referencia de el carrier") queda en cada viaje. No cambia el número del viaje ni montos.
const ETIQUETAS = [
  ["cargas", "Cargas en el archivo"],
  ["viajes", "Viajes con número de tráfico"],
  ["nuevos", "Nuevos"],
  ["cambian", "Cambian"],
  ["iguales", "Ya estaban"],
  ["con_varios", "Viajes con más de una carga"],
  ["tickets_a_crear", "Viajes que faltan y están en DAT (se crean)"],
  ["sin_viaje", "Cargas sin viaje"],
];

export function LoadTrafico() {
  const [archivo, setArchivo] = useState(null);
  const [res, setRes] = useState(null);
  const [simulado, setSimulado] = useState(false);
  const [busy, setBusy] = useState("");
  const [ultimo, setUltimo] = useState(null);

  const cargarUltimo = useCallback(async () => {
    try {
      const { data } = await api.get("/dat/trafico");
      setUltimo(data);
    } catch {
      setUltimo(null);
    }
  }, []);

  useEffect(() => { cargarUltimo(); }, [cargarUltimo]);

  const enviar = async (accion) => {
    if (!archivo) return;
    setBusy(accion);
    try {
      const fd = new FormData();
      fd.append("file", archivo);
      const { data } = await api.post(`/dat/trafico/${accion}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes({ ...data, accion });
      setSimulado(accion === "simular");
      if (accion === "aplicar") {
        const crear = data.encolados
          ? ` ${data.encolados} viajes se crean desde DAT ${data.sincronizacion_lanzada ? "ahora" : "en la próxima sincronización"} y reciben su número al terminar.`
          : "";
        toast.success(`Listo: ${data.actualizados} viajes con su número de tráfico.${crear}`);
        cargarUltimo();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo leer el archivo.");
    } finally {
      setBusy("");
    }
  };

  const conteos = res?.conteos || {};

  return (
    <Card className="mb-5" data-testid="load-trafico-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading">
          <FileSpreadsheet className="h-5 w-5 text-primary" /> Load de tráfico (archivo TRAFICO)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Sube el Excel de tráfico. De la hoja LOGISTICA USA se toma el número de load ("Referencia de el carrier")
          y se pone en cada viaje: se ve en el viaje y se encuentra con el buscador. No cambia el número del viaje ni
          los montos. Si una carga está en DAT pero la plataforma no tiene su viaje, el viaje se crea desde DAT.
        </p>
        {ultimo?.aplicado_en && (
          <p className="text-xs text-muted-foreground" data-testid="load-trafico-ultimo">
            Último archivo: {ultimo.archivo || "—"} · {String(ultimo.aplicado_en).slice(0, 16).replace("T", " ")} ·{" "}
            {ultimo.conteos?.viajes ?? 0} viajes con número · {ultimo.conteos?.sin_viaje ?? 0} cargas sin viaje
            {ultimo.tickets_pendientes > 0 && ` · ${ultimo.tickets_pendientes} viajes por crear desde DAT`}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="file"
            accept=".xlsx,.xlsm"
            className="max-w-sm"
            data-testid="load-trafico-archivo"
            onChange={(e) => { setArchivo(e.target.files?.[0] || null); setRes(null); setSimulado(false); }}
          />
          <Button variant="outline" disabled={!archivo || !!busy} onClick={() => enviar("simular")} data-testid="load-trafico-simular">
            {busy === "simular" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Simular
          </Button>
          <Button disabled={!archivo || !simulado || !!busy} onClick={() => enviar("aplicar")} data-testid="load-trafico-aplicar">
            {busy === "aplicar" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Aplicar
          </Button>
        </div>
        {busy && <p className="text-xs text-muted-foreground">Leyendo el archivo y buscando en DAT, puede tardar un minuto…</p>}

        {res && (
          <div className="space-y-3" data-testid="load-trafico-resultado">
            <p className="text-sm font-medium">{res.accion === "simular" ? "Simulación (no se guardó nada)" : "Aplicado"}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {ETIQUETAS.map(([k, label]) => (
                <div key={k} className="rounded-md border border-border bg-white p-2">
                  <div className="text-[11px] text-muted-foreground">{label}</div>
                  <div className="font-mono text-lg" data-testid={`load-trafico-conteo-${k}`}>{conteos[k] ?? 0}</div>
                </div>
              ))}
            </div>
            {(res.avisos || []).map((a) => <p key={a} className="text-xs text-amber-700">{a}</p>)}

            {res.tickets_a_crear?.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium">Viajes que faltan y están en DAT</p>
                <div className="max-h-64 overflow-auto rounded-md border border-border bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ticket DAT</TableHead><TableHead>Fecha</TableHead><TableHead>Referencia</TableHead>
                        <TableHead>Cliente en DAT</TableHead><TableHead>Load de tráfico</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {res.tickets_a_crear.map((t) => (
                        <TableRow key={t.ticket_id}>
                          <TableCell className="font-mono text-xs">{t.folio}</TableCell>
                          <TableCell className="text-xs">{t.fecha}</TableCell>
                          <TableCell className="text-xs">{t.referencia || "—"}</TableCell>
                          <TableCell className="text-xs">{t.cliente_dat || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{(t.loads || []).join(", ")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {res.sin_viaje?.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium">Cargas sin viaje en la plataforma ni en DAT</p>
                <div className="max-h-72 overflow-auto rounded-md border border-border bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fila</TableHead><TableHead>Load</TableHead><TableHead>Fecha</TableHead><TableHead>Factura</TableHead>
                        <TableHead>Referencia</TableHead><TableHead>Cliente</TableHead><TableHead>Por qué</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {res.sin_viaje.map((c) => (
                        <TableRow key={`${c.fila}-${c.load}`} data-testid={`load-trafico-sin-viaje-${c.load}`}>
                          <TableCell className="text-xs">{c.fila}</TableCell>
                          <TableCell className="font-mono text-xs">{c.load}</TableCell>
                          <TableCell className="text-xs">{c.fecha || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{c.factura || "—"}</TableCell>
                          <TableCell className="text-xs">{c.referencia || "—"}</TableCell>
                          <TableCell className="text-xs">{c.cliente || "—"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{c.motivo}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {res.asignaciones?.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium">Viajes que reciben o cambian su número de tráfico</p>
                <div className="max-h-72 overflow-auto rounded-md border border-border bg-white">
                  <Table>
                    <TableHeader><TableRow><TableHead>Viaje</TableHead><TableHead>Antes</TableHead><TableHead>Load de tráfico</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {res.asignaciones.slice(0, 300).map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-mono text-xs">{a.numero_load}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">{(a.antes || []).join(", ") || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{a.loads.join(", ")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
